import ClientsService from "../../../services/ClientsService/ClientsService.ts";
import VehicleService from "../../../services/VehicleService/VehicleService.ts";
import { isRecord } from "../../../services/Integrations/lifecycleValidation.ts";
import type { CanonicalVehicle } from "../../../types/lifecycle.ts";
import { PURPOSE_OPTIONS } from "../../../constants/vehiclePurposes.ts";
import {
  seedParty,
  type Party,
  type SearchInputs,
  VEHICLE_TYPE_OPTIONS,
} from "./model.ts";

export type WingRecord = Record<string, unknown>;
export type RecordEntry = {
  record: WingRecord | null;
  loaded: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saved?: boolean;
};
export const recordText = (value: unknown) => String(value ?? "").trim();
export const personKey = (pin: string) => pin.trim();
export const vehicleKey = (plate: string) => plate.trim().toUpperCase();
export const validPersonKey = (pin: string) =>
  /^\d{9}$|^\d{10}$|^\d{13}$/.test(personKey(pin));
const positiveId = (value: unknown) =>
  ["string", "number"].includes(typeof value) &&
  Number.isSafeInteger(Number(value)) &&
  Number(value) > 0;
const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export function readRecord(
  value: unknown,
  id: "CL_ID" | "CAR_ID",
  key: string
): WingRecord | null {
  if (Array.isArray(value) && !value.length) return null;
  if (isRecord(value) && !Object.keys(value).length) return null;
  if (!isRecord(value) || !positiveId(value[id]))
    throw new Error("Неуспешно търсене. Опитайте отново.");
  const matches =
    id === "CL_ID"
      ? personKey(recordText(value.EGN_INS)) === key
      : vehicleKey(recordText(value.REG_NO)) === key;
  if (!matches)
    throw new Error(
      "Получените данни не съответстват на търсенето. Опитайте отново."
    );
  return value;
}

export function partyFromRecord(
  record: WingRecord,
  saved = false
): Partial<Party> {
  const name = recordText(record.NAME_INS);
  const names = name.split(/\s+/);
  const legal =
    Number(record.TYPE_CLIENT) === 2 ||
    (!Number(record.TYPE_CLIENT) &&
      [9, 13].includes(recordText(record.EGN_INS).length));
  const party = seedParty({
    pin: recordText(record.EGN_INS),
    customerType: legal ? "legal" : "natural",
    companyName: legal ? name : "",
    firstName: legal ? "" : names[0],
    surname: !legal && names.length > 2 ? names.slice(1, -1).join(" ") : "",
    familyName: !legal && names.length > 1 ? names.at(-1) : "",
    street: recordText(record.ADDR_INS),
    phone: recordText(record.TEL_INS),
    settlementCode: recordText(record.EKATTE),
  });
  // An explicit save also propagates cleared fields to other roles sharing this
  // client. On initial lookup, blanks remain available for insurer gap-filling.
  if (saved) return party;
  // NAME_INS is one field in Wing: a two-part name must not acquire an insurer's
  // middle name. Other empty stored fields are gaps that the insurer can fill.
  return {
    ...Object.fromEntries(
      Object.entries(party).filter(([, value]) => value !== "")
    ),
    ...(name
      ? {
          firstName: party.firstName,
          surname: party.surname,
          familyName: party.familyName,
          companyName: party.companyName,
        }
      : {}),
  };
}

const VEHICLE_FIELDS = {
  make: "MARKA",
  model: "MODEL",
  chassis: "CHA_NO",
  productionYear: "CAR_YR",
  displacement: "VOLUME",
  enginePower: "MOTOR",
} as const;
const NUMERIC_FIELDS = new Set(["CAR_YR", "VOLUME", "MOTOR", "TYPE_CLIENT"]);
export function vehicleFromRecord(record: WingRecord): CanonicalVehicle {
  return Object.fromEntries(
    Object.entries(VEHICLE_FIELDS).flatMap(([key, field]) => {
      const value = recordText(record[field]);
      return value && (!NUMERIC_FIELDS.has(field) || Number(value) > 0)
        ? [[key, NUMERIC_FIELDS.has(field) ? Number(value) : value]]
        : [];
    })
  );
}
export const partyPayload = (
  party: Party,
  record: WingRecord | null
): WingRecord => ({
  CL_ID: Number(record?.CL_ID ?? 0),
  EGN_INS: personKey(party.pin),
  NAME_INS:
    party.customerType === "legal"
      ? party.companyName.trim()
      : [party.firstName, party.surname, party.familyName]
          .map(recordText)
          .filter(Boolean)
          .join(" "),
  ADDR_INS: party.street.trim(),
  TEL_INS: party.phone.trim(),
  EKATTE: party.settlementCode.trim(),
  TYPE_CLIENT: party.customerType === "legal" ? 2 : 1,
  // Omitted columns are preserved by client_write. Set a country only for new records.
  ...(!record ? { CNTRY: "BG" } : {}),
});

export type VehicleCatalogs = {
  types: Record<string, string>;
  usages: Record<string, string>;
};
const labelKey = (value: string) =>
  value
    .toLocaleLowerCase("bg")
    .replace(/[\s\p{P}\p{S}]+/gu, " ")
    .trim();
const TYPE_ALIASES: Record<string, string[]> = {
  passenger: ["Леки автомобили"],
  freight: ["Товарни автомобили"],
  bus: ["Автобуси"],
  motorcycle: ["Мотоциклет", "Мотоциклети и АТВ"],
  moped: ["Мотопеди"],
  trailer: ["Ремарке", "Ремаркета и полуремаркета"],
  tractor: ["Трактор", "Земеделска и горска техника"],
};
// Wing and the gateway use different labels for the same purposes. Resolve
// through the returned catalog: Wing codes are not insurer purpose IDs.
const PURPOSE_ALIASES: Record<string, string[]> = {
  "Лични нужди": ["Лично ползване"],
  "Фирмен автомобил за лична употреба": ["Служебно ползване"],
  "За транспорт на пари и ценности": ["Превоз на пари и ценности"],
  "Рент-а-кар": ["Отдаване под наем"],
  "Лизингов автомобил": ["МПС отдадени на лизинг"],
  "Охранителна дейност": ["Охранителна и детективска дейност"],
  Състезателен: ["Участия в състезания"],
};
const purposeLabels = (purpose: string) => [
  purpose,
  ...(PURPOSE_ALIASES[purpose] ?? []),
];
export function localCode(
  values: Record<string, string>,
  labels: string[]
): string {
  const wanted = labels.filter(Boolean).map(labelKey);
  const matches = Object.entries(values).filter(
    ([code, label]) => labels.includes(code) || wanted.includes(labelKey(label))
  );
  return matches.length === 1 ? matches[0][0] : "";
}
const typeLabels = (type: string) => [
  type,
  VEHICLE_TYPE_OPTIONS[type] ?? "",
  ...(TYPE_ALIASES[type] ?? []),
];
const matchesCode = (
  values: Record<string, string>,
  code: string,
  labels: string[]
) =>
  Boolean(
    code &&
      (labels.includes(code) ||
        labels
          .filter(Boolean)
          .map(labelKey)
          .includes(labelKey(values[code] ?? "")))
  );
export function vehicleInputsFromRecord(
  record: WingRecord,
  catalogs: VehicleCatalogs
): Partial<SearchInputs> {
  const types = Object.keys(VEHICLE_TYPE_OPTIONS).filter((type) =>
    matchesCode(catalogs.types, recordText(record.CAR_TYPE), typeLabels(type))
  );
  const purposes = Object.keys(PURPOSE_OPTIONS).filter((purpose) =>
    matchesCode(
      catalogs.usages,
      recordText(record.CAR_USAGE),
      purposeLabels(purpose)
    )
  );
  return {
    ...(recordText(record.CAR_TYPE) && types.length === 1
      ? { vehicleType: types[0] as SearchInputs["vehicleType"] }
      : {}),
    ...(recordText(record.CAR_USAGE) && purposes.length === 1
      ? { purpose: purposes[0] }
      : {}),
  };
}
export function vehiclePayload(
  vehicle: CanonicalVehicle,
  inputs: SearchInputs,
  record: WingRecord | null,
  catalogs: VehicleCatalogs
): WingRecord {
  const storedType = recordText(record?.CAR_TYPE);
  const storedUsage = recordText(record?.CAR_USAGE);
  const type = matchesCode(
    catalogs.types,
    storedType,
    typeLabels(inputs.vehicleType)
  )
    ? storedType
    : localCode(catalogs.types, typeLabels(inputs.vehicleType));
  const usage = matchesCode(
    catalogs.usages,
    storedUsage,
    purposeLabels(inputs.purpose)
  )
    ? storedUsage
    : localCode(catalogs.usages, purposeLabels(inputs.purpose));
  return {
    CAR_ID: Number(record?.CAR_ID ?? 0),
    REG_NO: vehicleKey(inputs.plate),
    TALON: inputs.certNumber.trim(),
    ...Object.fromEntries(
      Object.entries(VEHICLE_FIELDS).map(([key, field]) => [
        field,
        NUMERIC_FIELDS.has(field)
          ? Number(vehicle[key] || 0)
          : recordText(vehicle[key]),
      ])
    ),
    CAR_TYPE: type,
    CAR_USAGE: usage,
  };
}

export function changedRecord(payload: WingRecord, record: WingRecord | null) {
  if (!record) return true;
  return Object.entries(payload).some(([key, value]) => {
    if (key === "CL_ID" || key === "CAR_ID") return false;
    if (NUMERIC_FIELDS.has(key))
      return Number(value || 0) !== Number(record[key] || 0);
    if (key === "NAME_INS")
      return (
        recordText(value).replace(/\s+/g, " ") !==
        recordText(record[key]).replace(/\s+/g, " ")
      );
    if (key === "REG_NO")
      return (
        vehicleKey(recordText(value)) !== vehicleKey(recordText(record[key]))
      );
    return recordText(value) !== recordText(record[key]);
  });
}

/** Account-scoped cache: roles sharing an EGN/EIK share lookup and save state. */
export class LifecycleRecords {
  private listeners = new Set<() => void>();
  private state: Record<string, RecordEntry> = {};
  private reads = new Map<string, Promise<void>>();
  private catalogRead: Promise<VehicleCatalogs> | null = null;
  constructor(private jwt: string) {}
  getCatalogs = (retry = false): Promise<VehicleCatalogs> => {
    if (!this.catalogRead || retry)
      this.catalogRead = Promise.all([
        VehicleService.getVehicleTypes(this.jwt),
        VehicleService.getVehicleUsages(this.jwt),
      ]).then(([types, usages]) => {
        for (const values of [types, usages]) {
          if (
            !isRecord(values) ||
            Object.values(values).some((label) => typeof label !== "string")
          )
            throw new Error(
              "Неуспешно зареждане на видовете и предназначенията."
            );
        }
        return { types, usages };
      });
    return this.catalogRead;
  };
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.state;
  private update(key: string, entry: RecordEntry) {
    this.state = { ...this.state, [key]: entry };
    this.listeners.forEach((listener) => listener());
  }
  async load(kind: "client" | "vehicle", identity: string, retry = false) {
    const key = `${kind}:${identity}`;
    if (this.state[key]?.saving) return;
    if (this.reads.has(key)) return this.reads.get(key);
    if (this.state[key] && !retry) return;
    this.update(key, {
      record: this.state[key]?.record ?? null,
      loaded: false,
      loading: true,
      saving: false,
      error: null,
    });
    const read = (async () => {
      try {
        const value =
          kind === "client"
            ? await ClientsService.getClient(this.jwt, identity)
            : await VehicleService.getVehicle(identity, "", this.jwt);
        const record = readRecord(
          value,
          kind === "client" ? "CL_ID" : "CAR_ID",
          identity
        );
        this.update(key, {
          record,
          loaded: true,
          loading: false,
          saving: false,
          error: null,
        });
      } catch (error) {
        this.update(key, {
          record: null,
          loaded: false,
          loading: false,
          saving: false,
          error: errorMessage(error),
        });
      } finally {
        this.reads.delete(key);
      }
    })();
    this.reads.set(key, read);
    return read;
  }
  async save(
    kind: "client" | "vehicle",
    identity: string,
    payload: WingRecord
  ): Promise<boolean> {
    const key = `${kind}:${identity}`;
    const entry = this.state[key];
    if (!entry?.loaded || entry.loading || entry.saving) return false;
    const idField = kind === "client" ? "CL_ID" : "CAR_ID";
    const payloadIdentity =
      kind === "client"
        ? personKey(recordText(payload.EGN_INS))
        : vehicleKey(recordText(payload.REG_NO));
    if (
      payloadIdentity !== identity ||
      Number(payload[idField]) !== Number(entry.record?.[idField] ?? 0)
    )
      return false;
    this.update(key, { ...entry, saving: true, error: null });
    try {
      // A vehicle can have been created since the initial lookup. Merge that
      // record before allowing a write; never insert a duplicate or overwrite it.
      if (kind === "vehicle" && !entry.record) {
        const found = readRecord(
          await VehicleService.getVehicle(identity, "", this.jwt),
          "CAR_ID",
          identity
        );
        if (found) {
          this.update(key, {
            record: found,
            loaded: true,
            loading: false,
            saving: false,
            error:
              "Вече има запис за този автомобил. Данните са обновени; прегледайте ги преди запис.",
          });
          return false;
        }
      }
      const result =
        kind === "client"
          ? await ClientsService.saveClient(this.jwt, payload)
          : await VehicleService.saveVehicle(this.jwt, payload);
      const id = isRecord(result)
        ? result[kind === "client" ? "Client_ID" : "Car_ID"]
        : null;
      if (!positiveId(id))
        throw new Error("Записът не е потвърден. Проверете данните отново.");
      this.update(key, {
        record: {
          ...entry.record,
          ...payload,
          [kind === "client" ? "CL_ID" : "CAR_ID"]: Number(id),
        },
        loaded: true,
        loading: false,
        saving: false,
        error: null,
        saved: true,
      });
      return true;
    } catch (error) {
      this.update(key, { ...entry, saving: false, error: errorMessage(error) });
      return false;
    }
  }
}
